import type { Party } from "@/mocks/topics";

function stanceStyle(stance: string) {
  switch (stance) {
    case "质疑":
      return "bg-primary-100 text-primary-800";
    case "回应":
      return "bg-accent-100 text-accent-900";
    case "介入":
      return "bg-secondary-100 text-secondary-900";
    case "回避":
      return "bg-background-300 text-foreground-800";
    default:
      return "bg-background-200 text-foreground-700";
  }
}

function reliabilityLabel(value: number) {
  if (value >= 80) return "信息较完整";
  if (value >= 60) return "信息一般";
  if (value >= 45) return "信息偏少";
  return "关键信息缺失";
}

export default function PartyCompare({ parties }: { parties: Party[] }) {
  return (
    <section id="parties" className="scroll-mt-24">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            多方说法对比
          </h2>
          <p className="mt-2 font-label text-sm text-foreground-600">
            按「谁说的、说了什么、回避了什么」并列呈现，只对比表述口径，不替任何一方下结论。
          </p>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse bg-background-50 text-left">
            <thead>
              <tr className="bg-background-100 font-label text-xs text-foreground-600">
                <th scope="col" className="px-4 py-3 font-medium">相关方</th>
                <th scope="col" className="px-4 py-3 font-medium">立场</th>
                <th scope="col" className="px-4 py-3 font-medium">信息完整度</th>
                <th scope="col" className="px-4 py-3 font-medium">核心问题回应</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {parties.map((party) => (
                <tr key={party.name} className="font-label text-sm">
                  <td className="px-4 py-3">
                    <span className="block font-medium text-foreground-950">{party.name}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{party.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs ${stanceStyle(party.stance)}`}>
                      {party.stance}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-background-200">
                        <span
                          className="block h-full rounded-full bg-secondary-500"
                          style={{ width: `${party.reliability}%` }}
                        ></span>
                      </span>
                      <span className="text-xs text-foreground-600">
                        {reliabilityLabel(party.reliability)}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-600">
                    {party.stance === "回避" ? "未正面回应" : "已给出公开说明"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        {parties.map((party) => (
          <article
            key={party.name}
            className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 font-label text-xs font-medium ${stanceStyle(party.stance)}`}>
                {party.stance}
              </span>
              <span className="font-label text-xs text-foreground-500">{party.role}</span>
            </div>

            <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
              {party.name}
            </h3>

            <blockquote className="mt-3 border-l-2 border-primary-400 bg-background-100 py-3 pl-4 pr-3 font-label text-sm italic leading-relaxed text-foreground-700">
              「{party.quote}」
            </blockquote>

            <ul className="mt-4 space-y-2.5">
              {party.points.map((point) => (
                <li key={point} className="flex gap-2 font-label text-sm leading-relaxed text-foreground-600">
                  <i className="ri-checkbox-blank-circle-line mt-1.5 flex-none text-[8px] text-primary-500"></i>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}